import { Button } from 'primereact/button';
import { QRCodeSVG } from 'qrcode.react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Offer {
    date: string;
    plan_title: string;
    service: string;
    coverage: string;
    cardNumber: string;
    validity: string;
    esimQr: string;
}

interface InstallationGuideProps {
    data: Offer;
}

const InstallationGuide = ({ data }: InstallationGuideProps) => {
    const [activePlatform, setActivePlatform] = useState<'ios' | 'android'>('ios');

    const qrText = data.esimQr;
    const { t } = useTranslation();

    return (
        <div className="w-full py-6">
            <h3 className="text-2xl font-bold text-center mb-6">  {t('installation_guide.installation_guide_title')}</h3>

            <div className="flex gap-2 justify-center mb-4 bg-gray-100 border-round-3xl p-1">
                <Button
                    className={`w-full border-round-3xl font-semibold transition-colors text-gray-900 border-none flex justify-content-center align-items-center
        ${activePlatform === 'ios' ? 'bg-white border-none' : 'bg-transparent text-gray-600'}`}
                    onClick={() => setActivePlatform('ios')}
                >
                    {t('installation_guide.ios_setup_guide.title')}
                </Button>
                <Button
                    className={`w-full border-round-3xl font-semibold transition-colors text-gray-900 border-none flex justify-content-center align-items-center
        ${activePlatform === 'android' ? 'bg-white border-none' : 'bg-transparent text-gray-600'}`}
                    onClick={() => setActivePlatform('android')}
                >
                    {t('installation_guide.android_setup_guide.title')}
                </Button>
            </div>

            {/* Steps Grid */}
            <div className="grid">
                {/* Step 1: Setup eSIM */}
                <div className="col-12 md:col-6 flex flex-column align-items-center text-center p-4">
                    <span className="bg-blue-900 text-white px-4 py-1 border-round-xl mb-2 text-sm"> {t('installation_guide.ios_setup_guide.step_01_title')}</span>
                    <h4 className="font-bold mb-1">{t('installation_guide.ios_setup_guide.step_01_heading')}</h4>
                    <p className="text-sm mb-2 white-space-normal">{t('installation_guide.ios_setup_guide.step_01_description')}</p>
                    <Button className="bg-blue-900 text-white px-4 py-2 border-round flex gap-2 items-center mb-2">
                        {t('installation_guide.ios_setup_guide.step_01_heading')} {activePlatform === 'ios' ? <i className="pi pi-apple"></i> : <i className="pi pi-google"></i>}
                    </Button>
                    <QRCodeSVG value={qrText} size={128} />
                </div>

                {/* Step 2 */}
                <div className="col-12 md:col-6 flex flex-column align-items-center text-center p-4">
                    <span className="bg-blue-900 text-white px-4 py-1 border-round-xl mb-2 text-sm"> {t('installation_guide.ios_setup_guide.step_02_title')}</span>
                    <h4 className="font-bold mb-1">{t('installation_guide.ios_setup_guide.step_02_heading')}</h4>
                    <p className="text-sm white-space-normal">{t('installation_guide.ios_setup_guide.step_02_description')}</p>
                </div>

                {/* Step 3 */}
                <div className="col-12 md:col-6 flex flex-column align-items-center text-center p-4">
                    <span className="bg-blue-900 text-white px-4 py-1 border-round-xl mb-2 text-sm">{t('installation_guide.ios_setup_guide.step_03_title')}</span>
                    <h4 className="font-bold mb-1">{t('installation_guide.ios_setup_guide.step_03_heading')}</h4>
                    <p className="text-sm white-space-normal">
                        {activePlatform === 'ios'
                            ? t('installation_guide.ios_setup_guide.step_03_description')
                            : t('installation_guide.android_setup_guide.step_03_description')}
                    </p>
                </div>

                {/* Step 4 */}
                <div className="col-12 md:col-6 flex flex-column align-items-center text-center p-4">
                    <span className="bg-blue-900 text-white px-4 py-1 border-round-xl mb-2 text-sm">{t('installation_guide.ios_setup_guide.step_04_title')}</span>
                    <h4 className="font-bold mb-1">{t('installation_guide.ios_setup_guide.step_04_heading')}</h4>
                    <p className="text-sm white-space-normal">
                        {activePlatform === 'ios'
                            ? t('installation_guide.ios_setup_guide.step_04_description')
                            : t('installation_guide.android_setup_guide.step_04_description')}
                    </p>
                </div>
            </div>

            {/* Pro-Tip Box */}
            <div className="mt-6 max-w-2xl mx-auto p-4 border border-blue-300 rounded-md text-sm bg-blue-50">
                💡 <strong>{t('installation_guide.ios_setup_guide.pro_tip')}</strong>
                <ol className="ml-4 list-decimal">
                    <li>{t('installation_guide.ios_setup_guide.pro_tip_description')}</li>
                    <li>
                        <a href="https://api.whatsapp.com/send/?phone=15558043874&text=Hi+I%27d+like+to+understand+how+you+can+help+on+my+connectivity+needs&type=phone_number&app_absent=0" target="_blank" rel="noopener noreferrer">
                            {t('installation_guide.ios_setup_guide.support')}
                        </a>.
                    </li>
                </ol>
            </div>
        </div>
    );
};

export default InstallationGuide;